import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import {
  createZeroFallbackBlurHandler,
  hasStraySecondDecimalSeparator,
  isDecimalSeparatorPrepended,
  isNoOpAfterNormalization,
  moveCaretAfterDecimal,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import {
  DetailsTrailing,
  StyledInput,
  StyledLabel,
  StyledNutritionItem,
} from "./styled"

interface IProps {
  label: string
  value: number
  onChange: (value: number) => void
  placeholder?: string
  detailTrailing?: string
  setIsFormValid?: (isValid: boolean) => void
  disabled?: boolean
  paddingRightOffset?: number
  decimalPlaces?: number
}

const NutritionFormItem = (props: IProps) => {
  const {
    value,
    onChange,
    setIsFormValid,
    detailTrailing,
    disabled,
    paddingRightOffset,
    decimalPlaces = 0,
  } = props
  const [internalValue, setInternalValue] = useState(value?.toString() ?? "")
  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)
  const pendingDecimalResetRef = useRef(false)
  const hasInitRef = useRef(false)

  const inputRef = useRef<HTMLInputElement>(null)

  // Init: populate internalValue once, the first time value is a real number.
  // value can start null/NaN (e.g. async-loaded food data) and become valid after mount.
  useEffect(() => {
    if (hasInitRef.current) return

    const hasNumberValue = value !== null && !isNaN(value)
    if (hasNumberValue) {
      setInternalValue(value.toString())
      hasInitRef.current = true
    }
  }, [value])

  // Update: keep internalValue in sync when value changes externally
  // (e.g. macros recalculated when calories change), without stomping
  // an in-progress edit (the "." reset placeholder).
  useEffect(() => {
    const hasNumberValue = value !== null && !isNaN(value)
    const isValueChanged = value !== Number(internalValue)
    if (hasNumberValue && isValueChanged && internalValue !== ".") {
      setInternalValue(value.toString())
    }
  }, [value])

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled) {
      return
    }

    if (pendingDecimalResetRef.current) {
      pendingDecimalResetRef.current = false
      setIsFormValid?.(true)
      setInternalValue(".")
      setTimeout(() => moveCaretAfterDecimal(inputRef.current), 20)
      return
    }

    if (values.floatValue !== value) {
      onChange?.(values.floatValue ?? 0)
    }

    const normalized = normalizeNumericInputValue(values.value)

    setIsFormValid?.(normalized !== "")

    setInternalValue(normalized)
  }

  const isAllowed = (values: NumberFormatValues) => {
    pendingDecimalResetRef.current = false

    const rawValue = inputRef.current?.value ?? values.value

    if (isDecimalSeparatorPrepended(rawValue, internalValue)) {
      pendingDecimalResetRef.current = true
      return true
    }

    if (hasStraySecondDecimalSeparator(rawValue, internalValue)) {
      return false
    }

    const normalized = normalizeNumericInputValue(values.value)
    return !isNoOpAfterNormalization(values.value, normalized, internalValue)
  }

  useLayoutEffect(() => {
    setTrailWidth(trailingRef.current?.getBoundingClientRect().width ?? 0)
  }, [trailingRef, detailTrailing])

  const onBlur = createZeroFallbackBlurHandler({
    value,
    internalValue,
    onChange,
    setInternalValue,
    inputRef,
  })

  return (
    <StyledNutritionItem
      style={
        {
          "--input-padding-right": detailTrailing ? "16px" : "16px",
          "--input-trailing-padding-left": detailTrailing ? "8px" : "0px",
          "--detail-trailing-width": `${
            trailingWidth - (paddingRightOffset ?? 0)
          }px`,
        } as React.CSSProperties
      }
    >
      {props.label && <StyledLabel>{props.label}</StyledLabel>}

      <NumericFormat
        getInputRef={inputRef}
        allowedDecimalSeparators={[",", "."]}
        isAllowed={isAllowed}
        customInput={StyledInput}
        inputMode={decimalPlaces > 0 ? "decimal" : "numeric"}
        decimalScale={decimalPlaces}
        allowNegative={false}
        thousandSeparator=","
        disabled={disabled}
        style={{
          paddingRight:
            "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
        }}
        onBlur={onBlur}
        $isError={false}
        placeholder={props.placeholder}
        value={internalValue ?? ""}
        valueIsNumericString
        onValueChange={onValueChange}
        autoComplete="new-password"
      />
      {detailTrailing && <DetailsTrailing>{detailTrailing}</DetailsTrailing>}

      {detailTrailing && (
        <DetailsTrailing style={{ visibility: "hidden" }} ref={trailingRef}>
          {detailTrailing}
        </DetailsTrailing>
      )}
    </StyledNutritionItem>
  )
}

export default NutritionFormItem
